import { v1 } from 'uuid';
import CookieUtils from '../framework/utils/CookieUtils';
import md5 from 'md5';
import _get from 'lodash/get';
class GoogleTagManager {
    constructor() {
        Object.defineProperty(this, "originalLocation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "searchPageView", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isFinishedRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pushEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                this.getDataLayer().push(e);
            }
        });
        Object.defineProperty(this, "pushPageView", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (pathname, search, title, variant) => {
                this.pushEvent({
                    event: 'Pageview',
                    page: `${pathname}${search}`,
                    location: this.originalLocation,
                    title: title ? title : this.defaultTitle,
                    // Autobahn BL-07: only present for -next pilot pageviews (undefined -> key omitted for legacy).
                    ...(variant ? { variant } : {})
                });
            }
        });
        Object.defineProperty(this, "pushSearchPageView", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (pathname, search, title, variant) => {
                if (this.searchPageView && this.searchPageView.pathname === pathname && this.searchPageView.search === search) {
                    this.pushPageView(pathname, search, title, variant);
                    this.searchPageView = null;
                }
            }
        });
        Object.defineProperty(this, "pushSearchResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (cars) => {
                this.pushEvent({
                    ecommerce: {
                        currencyCode: 'EUR',
                        impressions: cars
                    }
                });
            }
        });
        Object.defineProperty(this, "pushCarIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ids, userEmail) => {
                const email = userEmail ? md5(userEmail) : '';
                this.pushEvent({
                    event: 'crto_listingpage',
                    crto: {
                        email,
                        products: ids
                    }
                });
            }
        });
        Object.defineProperty(this, "pushOfferRequestEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, currentSalePrice, userEmail) => {
                this.pushEvent({
                    event: 'OfferRequest',
                    eventCategory: 'Lead',
                    eventAction: 'Form',
                    eventLabel: CookieUtils.getCookieId(),
                    CurrentSalesPrice: String(currentSalePrice),
                    UserEmail: userEmail,
                    CarId: carId
                });
            }
        });
        Object.defineProperty(this, "pushOfferRequestPurchaseEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (car, financing) => {
                this.pushEvent({
                    event: 'purchase',
                    ecommerce: {
                        purchase: {
                            actionField: {
                                id: v1(),
                                affiliation: car.metaData.ownerId,
                                revenue: String(car.price.currentSalesPrice),
                                tax: String(car.price.currentSalesPrice * car.price.vatRate / 100) // eslint-disable-line no-magic-numbers
                            },
                            products: [{
                                    id: car._id,
                                    name: `${car.mainData.make} ${car.mainData.model}`,
                                    brand: car.mainData.make,
                                    price: String(car.price.currentSalesPrice),
                                    category: `${car.mainData.make}/${car.mainData.model}/${car.mainData.make} ${car.mainData.model}`,
                                    quantity: 1
                                }, {
                                    name: car.financing.bank ? car.financing.bank : financing.financingInstitution,
                                    brand: financing.financingInstitution,
                                    price: String(financing.monthlyInstallment),
                                    category: 'Payment',
                                    metric1: String(financing.firstInstallment),
                                    metric2: financing.paybackPeriod,
                                    metric3: financing.annualMileage,
                                    metric4: String(financing.finalInstallment),
                                    metric5: String(financing.nominalInterestRate),
                                    metric6: String(financing.annualPercentageRate),
                                    metric7: String(financing.totalInterestPayments),
                                    metric8: String(financing.grossLoanAmount),
                                    metric9: String(financing.onetimeSpecialPayment),
                                    quantity: 1
                                }]
                        }
                    }
                });
            }
        });
        Object.defineProperty(this, "pushVehicleDetailedViewEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (car, financing) => {
                this.pushEvent({
                    ecommerce: {
                        detail: {
                            actionField: {
                                id: (Math.floor(Math.random() * Date.now())).toString(16),
                                affiliation: car.metaData ? car.metaData.ownerId : '',
                                revenue: String(car.price.currentSalesPrice),
                                tax: String(car.price.currentSalesPrice * car.price.vatRate / 100) // eslint-disable-line no-magic-numbers
                            },
                            products: [{
                                    id: car._id,
                                    name: `${car.mainData.make} ${car.mainData.model}`,
                                    brand: car.mainData.make,
                                    price: String(car.price.currentSalesPrice),
                                    category: `${car.mainData.make}/${car.mainData.model}/${car.mainData.make} ${car.mainData.model}`,
                                    firstRegistrationYear: `${car.mainData.firstRegistration}`,
                                    mileage: `${car.mainData.mileage}`,
                                    quantity: 1
                                }, {
                                    name: car.financing.bank ? car.financing.bank : financing.financingInstitution,
                                    brand: financing.financingInstitution,
                                    price: String(financing.monthlyInstallment),
                                    category: 'Payment',
                                    metric1: String(financing.firstInstallment),
                                    metric2: financing.paybackPeriod,
                                    metric3: financing.annualMileage,
                                    metric4: String(financing.finalInstallment),
                                    metric5: String(financing.nominalInterestRate),
                                    metric6: String(financing.annualPercentageRate),
                                    metric7: String(financing.totalInterestPayments),
                                    metric8: String(financing.grossLoanAmount),
                                    metric9: String(financing.onetimeSpecialPayment),
                                    quantity: 1
                                }]
                        }
                    }
                });
            }
        });
        /**
         * ADV-183 — GA4-compatible `view_item` ecommerce event, sourced entirely from the
         * backend car object (never scraped from rendered HTML). Fired once per vehicle
         * detail page load, unconditionally (unlike the legacy pushVehicleDetailedViewEvent,
         * which is gated on metaData.ownerId being present).
         *
         * `item_id` is the existing VDP UUID (car._id). `vehicle_id` is set to the SAME
         * UUID today because that's what the Meta feed (export-cars fb/facebookshop
         * transformers, ADV-200) actually emits as of 2026-08-20 — the ticket's
         * "000AD1"-style example is the pre-ADV-200 short code and is stale. If the
         * tracking agency confirms they need the short metaData.vehicleId code instead,
         * swap the `vehicle_id` line below for `car.metaData?.vehicleId` — everything
         * else about this event is unaffected.
         *
         * Per GA4 convention, `{ ecommerce: null }` is pushed immediately before the
         * event to clear any previously set ecommerce object (avoids Google Analytics
         * merging objects across pushes).
         */
        Object.defineProperty(this, "pushViewItemEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (car) => {
                const price = _get(car, 'price.currentSalesPrice', null);
                const make = _get(car, 'mainData.make', '');
                const model = _get(car, 'mainData.model', '');
                const variant = _get(car, 'mainData.variant', '') || _get(car, 'mainData.trimLine', '');
                const carId = _get(car, '_id', '');
                this.pushEvent({ ecommerce: null });
                this.pushEvent({
                    event: 'view_item',
                    ecommerce: {
                        currency: 'EUR',
                        value: price,
                        items: [{
                                item_id: carId,
                                // See doc comment above — same as item_id until the agency confirms otherwise.
                                vehicle_id: carId,
                                item_name: `${make} ${model}`.trim(),
                                item_brand: make,
                                item_variant: variant,
                                price,
                                quantity: 1
                            }]
                    }
                });
            }
        });
        Object.defineProperty(this, "pushVehicleDetailedUserEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, userEmail) => {
                const email = userEmail ? md5(userEmail) : '';
                this.pushEvent({
                    event: 'crto_productpage',
                    crto: {
                        email,
                        products: [carId]
                    }
                });
            }
        });
        Object.defineProperty(this, "pushSuccessDealerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFinishedRegistration = true;
                this.pushEvent({
                    event: 'NewSignUp',
                    eventCategory: 'Registration',
                    eventAction: 'Success',
                    eventLabel: 'Dealer'
                });
            }
        });
        Object.defineProperty(this, "pushFailureDealerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (errorMessage) => {
                this.pushEvent({
                    event: 'NewSignUp',
                    eventCategory: 'Registration',
                    eventAction: 'Failure',
                    eventLabel: errorMessage
                });
            }
        });
        Object.defineProperty(this, "pushSuccessCustomerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFinishedRegistration = true;
                this.pushEvent({
                    event: 'NewSignUp',
                    eventCategory: 'Registration',
                    eventAction: 'Success',
                    eventLabel: 'Customer'
                });
            }
        });
        Object.defineProperty(this, "pushFailureCustomerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (errorMessage) => {
                this.pushEvent({
                    event: 'NewSignUp',
                    eventCategory: 'Registration',
                    eventAction: 'Failure',
                    eventLabel: errorMessage
                });
            }
        });
        Object.defineProperty(this, "startRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFinishedRegistration = false;
            }
        });
        Object.defineProperty(this, "endDealerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.isFinishedRegistration) {
                    this.isFinishedRegistration = true;
                    this.pushEvent({
                        event: 'NewSignUp',
                        eventCategory: 'Registration',
                        eventAction: 'Abort',
                        eventLabel: 'Dealer'
                    });
                }
            }
        });
        Object.defineProperty(this, "endCustomerRegistration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.isFinishedRegistration) {
                    this.pushEvent({
                        event: 'NewSignUp',
                        eventCategory: 'Registration',
                        eventAction: 'Abort',
                        eventLabel: 'Customer'
                    });
                }
            }
        });
        Object.defineProperty(this, "historyListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ({ pathname, search }, action) => {
                const firstSegment = pathname.split('/')[1];
                // BL-07: '/search*' pageviews are deferred — the owning page flushes the enriched event
                // itself via pushSearchPageView (see SearchNextPage / VehicleDetailedNextPage), instead of
                // this firing an immediate generic Pageview (which raced/duplicated the enriched one).
                if (firstSegment === 'search') {
                    this.searchPageView = {
                        pathname,
                        search
                    };
                }
                else {
                    this.pushPageView(pathname, search);
                }
            }
        });
        Object.defineProperty(this, "setDefaultTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (title) => {
                this.defaultTitle = title;
            }
        });
        Object.defineProperty(this, "setOriginalLocation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (protocol, hostname, pathname, search) => {
                this.originalLocation = `${protocol}//${hostname}${pathname}${search}`;
            }
        });
        Object.defineProperty(this, "getDataLayer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // @ts-ignore
                return window.dataLayer = window.dataLayer || [];
            }
        });
    }
    init() {
        const { location: { protocol, hostname, pathname, search } } = document;
        this.setOriginalLocation(protocol, hostname, pathname, search);
        this.isFinishedRegistration = true;
    }
}
export default new GoogleTagManager();
